/**
 * FGA API Service (Placeholder)
 *
 * This service provides HTTP client methods for communicating with the FGA proxy API.
 * Currently implemented as a placeholder - full implementation will be added in a future feature.
 *
 * @packageDocumentation
 */

import { Injectable, inject } from '@angular/core';
import { HttpClient, HttpErrorResponse } from '@angular/common/http';
import { catchError, map, Observable, of, throwError } from 'rxjs';

import { FgaGroupsResponse, FgaGroupUsersResponse } from '../models/group.model';
import { FgaUserGroupsResponse } from '../models/user.model';
import { FGA_ORGANIZATION_PREFIX, FGA_GROUP_PREFIX, FGA_USER_PREFIX } from '../models/fga-constants';
import { LoggerService } from './logger.service';

/**
 * Service for making HTTP requests to the FGA proxy API.
 *
 * Provides read-only operations for querying FGA groups and group memberships.
 * Create/delete operations are placeholders for future implementation.
 *
 * The service uses the AuthInterceptor to automatically attach
 * Bearer tokens to all requests.
 *
 * @remarks
 * - Implemented: getGroups, getGroupUsers, getGroupOwners, getGroupMembers
 * - Placeholders: createGroup, deleteGroup
 * - User operations handled by UserEnrichmentService (Auth0 Management API)
 *
 * @example
 * ```typescript
 * const fgaApi = inject(FgaApiService);
 *
 * // Configure the API base URL
 * fgaApi.setApiBase('http://localhost:3000');
 *
 * // Fetch groups
 * fgaApi.getGroups('org_demo').subscribe(response => {
 *   console.log('Groups:', response.groups);
 * });
 * ```
 */
@Injectable({ providedIn: 'root' })
export class FgaApiService {
  private readonly http = inject(HttpClient);
  private readonly logger = inject(LoggerService);

  /**
   * Base URL for the FGA proxy API.
   * Set via setApiBase() before making API calls.
   */
  private _apiBase = '';
  private _storeId = '';
  private _subscriptionKey = '';

  /**
   * Sets the base URL for API requests.
   *
   * @param apiBase - Base URL (e.g., 'http://localhost:3000' or '/api')
   */
  setApiBase(apiBase: string): void {
    // Remove trailing slash for consistency
    this._apiBase = apiBase.replace(/\/$/, '');
  }

  setStoreId(storeId: string): void {
    this._storeId = storeId;
  }

  setSubscriptionKey(key: string): void {
    this._subscriptionKey = key;
  }

  /**
   * Gets the current API base URL.
   */
  get apiBase(): string {
    return this._apiBase;
  }

  private getHeaders() {
    const headers: Record<string, string> = {};
    if (this._subscriptionKey) {
      headers['ocp-apim-subscription-key'] = this._subscriptionKey;
    }
    return headers;
  }

  private getEndpoint(path: string): string {
    let endpoint: string;
    if (this._storeId) {
      // Direct FGA API structure: /stores/{store_id}/{path}
      endpoint = `${this._apiBase}/stores/${this._storeId}/${path}`;
    } else {
      // Legacy/Proxy structure: /{path}
      endpoint = `${this._apiBase}/${path}`;
    }
    this.logger.debug('FgaApiService', 'Request URL', { endpoint });
    return endpoint;
  }

  // ---------------------------------------------------------------------------
  // Authorization Checks
  // ---------------------------------------------------------------------------

  /**
   * Checks whether a user has a given relation to an object in the FGA store.
   *
   * Maps directly to the FGA `/check` endpoint:
   * `{ tuple_key: { user, relation, object } }`
   *
   * @param user - Full FGA user identifier (e.g., "user:waad|org|abc123")
   * @param relation - Relation to check (e.g., "authz_admin")
   * @param object - Full FGA object identifier (e.g., "organization:org_demo")
   * @returns Observable emitting `true` when the relation exists, `false` otherwise
   */
  checkPermission(user: string, relation: string, object: string): Observable<boolean> {
    const payload = {
      tuple_key: { user, relation, object },
    };

    return this.http
      .post<{ allowed?: boolean }>(this.getEndpoint('check'), payload, {
        headers: this.getHeaders(),
      })
      .pipe(
        map(response => response.allowed === true),
        catchError((error: HttpErrorResponse) => {
          const message =
            error.error?.error || error.error?.message || error.message || 'Failed to check permission';
          return throwError(() => new Error(message));
        })
      );
  }

  // ---------------------------------------------------------------------------
  // Group Operations (Placeholder)
  // ---------------------------------------------------------------------------

  /**
   * Fetches all groups for an organisation.
   *
   * Queries the FGA store for all tuples matching the pattern:
   * `{ object: "group:<organisationId>|*" }`
   *
   * @param organisationId - Organisation ID to fetch groups for
   * @returns Observable of groups response containing full group IDs (e.g., "group:org_demo|engineering")
   *
   * @example
   * ```typescript
   * this.fgaApi.getGroups('org-test1').subscribe(response => {
   *   console.log('Groups:', response.groups); // ['group:engineering', 'group:sales']
   * });
   * ```
   */
  getGroups(organisationId: string): Observable<FgaGroupsResponse> {
    // Query FGA for groups linked to an organisation
    // FGA model: group:X → groups → organisation:Y
    // So we query: { object: "organisation:{id}", relation: "groups" }
    // 
    // Note: Uses "organization" (US spelling) per OpenFGA conventions
    const payload = {
      tuple_key: {
        object: `${FGA_ORGANIZATION_PREFIX}${organisationId}`,
        relation: 'groups'
      },
      page_size: 100
    };

    return this.http.post<{ tuples: { key: { object: string; relation: string; user: string } }[]; continuation_token?: string }>(
      this.getEndpoint('read'),
      payload,
      { headers: this.getHeaders() }
    ).pipe(
      // Map the FGA read response to our domain model
      // Extract group IDs from the 'user' field of tuples
      // FGA stores: user: "group:X", relation: "groups", object: "organisation:Y"
      (source) => new Observable<FgaGroupsResponse>(subscriber => {
        source.subscribe({
          next: (response) => {
            // Extract group IDs from the 'user' field
            const groupIds = new Set<string>();
            
            response.tuples?.forEach(tuple => {
              // The 'user' field contains the group identifier (e.g., "group:engineering")
              if (tuple.key.user?.startsWith(FGA_GROUP_PREFIX)) {
                groupIds.add(tuple.key.user);
              }
            });

            subscriber.next({
              groups: Array.from(groupIds).sort(),
              organisationId,
              continuationToken: response.continuation_token
            });
            subscriber.complete();
          },
          error: (error) => subscriber.error(error)
        });
      })
    );
  }

  /**
   * Fetches users that belong to a specific group.
   *
   * @param groupId - Full FGA group identifier
   * @returns Observable of group users response
   *
   * @todo Implement actual HTTP call when proxy endpoint is ready
   */
  getGroupUsers(groupId: string, relation: string): Observable<FgaGroupUsersResponse> {
    const payload = {
      tuple_key: {
        object: groupId,
        relation,
      },
      page_size: 100,
    };
    return this.http
      .post<{ tuples?: { key?: { user?: string } }[]; continuation_token?: string }>(
        this.getEndpoint('read'),
        payload,
        { headers: this.getHeaders() }
      )
      .pipe(
        map(response => {
          const users = new Set<string>();
          response.tuples?.forEach(t => {
            const u = t.key?.user;
            if (u?.startsWith(FGA_USER_PREFIX)) users.add(u);
          });
          // Fix type mismatch by including `groupId` in the returned object
          return {
            groupId,
            users: Array.from(users).sort(),
            continuationToken: response.continuation_token,
          };
        })
      );
  }

  getGroupOwners(groupId: string): Observable<FgaGroupUsersResponse> {
    return this.getGroupUsers(groupId, 'admin');
  }

  getGroupMembers(groupId: string): Observable<FgaGroupUsersResponse> {
    return this.getGroupUsers(groupId, 'member');
  }

  /**
   * Fetches groups that a user belongs to for a specific relation.
   *
   * Uses the FGA `list-objects` endpoint with:
   * `{ user, relation, type: 'group' }`.
   *
   * @param userId - Full FGA user identifier (e.g., "user:waad|org|abc123")
   * @param relation - Group relation to resolve (e.g., "member", "admin")
   * @returns Observable containing group IDs for the user
   */
  getUserGroupsByRelation(userId: string, relation: 'member' | 'admin'): Observable<FgaUserGroupsResponse> {
    const payload = {
      user: userId,
      relation,
      type: 'group',
    };

    return this.http
      .post<{ objects?: string[] }>(this.getEndpoint('list-objects'), payload, {
        headers: this.getHeaders(),
      })
      .pipe(
        map(response => {
          const groups = (response.objects ?? []).filter(groupId => groupId.startsWith(FGA_GROUP_PREFIX));

          return {
            userId,
            groups: Array.from(new Set(groups)).sort(),
          };
        }),
        catchError((error: HttpErrorResponse) => {
          const message =
            error.error?.error || error.error?.message || error.message || 'Failed to fetch user groups';
          return throwError(() => new Error(message));
        })
      );
  }

  /**
   * Creates a new group by writing an FGA tuple.
   *
   * Creates a tuple linking the group to the organization:
   * `user: "group:{orgId}|{name}", relation: "groups", object: "organization:{orgId}"`
   *
   * @param organisationId - Organisation ID
   * @param groupName - Name of the group to create
   * @returns Observable completing with the created group ID
   *
   * @example
   * ```typescript
   * this.fgaApi.createGroup('org_demo', 'new-team').subscribe({
   *   next: (result) => console.log('Created:', result.groupId),
   *   error: (err) => console.error('Failed:', err.message)
   * });
   * ```
   */
  createGroup(organisationId: string, groupName: string): Observable<{ groupId: string }> {
    const groupId = `${FGA_GROUP_PREFIX}${organisationId}|${groupName}`;

    const payload = {
      writes: {
        tuple_keys: [
          {
            user: groupId,
            relation: 'groups',
            object: `${FGA_ORGANIZATION_PREFIX}${organisationId}`,
          },
        ],
      },
    };

    return this.http
      .post<Record<string, never>>(this.getEndpoint('write'), payload, {
        headers: this.getHeaders(),
      })
      .pipe(
        map(() => ({ groupId })),
        catchError((error: HttpErrorResponse) => {
          const message =
            error.error?.error || error.error?.message || error.message || 'Failed to create group';
          return throwError(() => new Error(message));
        })
      );
  }

  /**
   * Deletes a group.
   *
   * @param groupId - Full FGA group identifier
   * @returns Observable completing on success
   *
   * @todo Implement actual HTTP call when proxy endpoint is ready
   */
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  deleteGroup(_groupId: string): Observable<void> {
    // Placeholder
    return of(undefined);
  }

  // ---------------------------------------------------------------------------
  // Organisation Member Operations
  // ---------------------------------------------------------------------------

  /**
   * Fetches all users that are members of an organisation.
   *
   * Queries FGA for tuples matching:
   * `{ object: "organization:{orgId}", relation: "member", user: "user:*" }`
   *
   * @param organisationId - Organisation ID to fetch members for
   * @returns Observable of user IDs that are organisation members
   *
   * @example
   * ```typescript
   * this.fgaApi.getOrganisationMembers('org-test1').subscribe(users => {
   *   console.log('Org members:', users); // ['user:waad|org|abc123', ...]
   * });
   * ```
   */
  getOrganisationMembers(organisationId: string): Observable<string[]> {
    const payload = {
      tuple_key: {
        object: `${FGA_ORGANIZATION_PREFIX}${organisationId}`,
        relation: 'member'
      },
      page_size: 100
    };

    return this.http.post<{ tuples?: { key?: { user?: string } }[]; continuation_token?: string }>(
      this.getEndpoint('read'),
      payload,
      { headers: this.getHeaders() }
    ).pipe(
      map(response => {
        const users = new Set<string>();
        response.tuples?.forEach(t => {
          const u = t.key?.user;
          if (u?.startsWith(FGA_USER_PREFIX)) users.add(u);
        });
        return Array.from(users).sort();
      }),
      catchError((error: HttpErrorResponse) => {
        const message =
          error.error?.error || error.error?.message || error.message || 'Failed to fetch organisation members';
        return throwError(() => new Error(message));
      })
    );
  }

  // ---------------------------------------------------------------------------
  // Group Membership Operations
  // ---------------------------------------------------------------------------

  /**
   * Adds a user as a member of a group.
   *
   * Creates an FGA tuple: `{ user: "{userId}", relation: "member", object: "{groupId}" }`
   *
   * @param groupId - Full FGA group identifier (e.g., "group:org|team")
   * @param userId - Full FGA user identifier (e.g., "user:waad|org|abc123")
   * @returns Observable completing on success
   */
  addUserToGroup(groupId: string, userId: string): Observable<void> {
    const payload = {
      writes: {
        tuple_keys: [
          {
            user: userId,
            relation: 'member',
            object: groupId,
          },
        ],
      },
    };

    return this.http
      .post<Record<string, never>>(this.getEndpoint('write'), payload, {
        headers: this.getHeaders(),
      })
      .pipe(
        map(() => undefined),
        catchError((error: HttpErrorResponse) => {
          const message =
            error.error?.error || error.error?.message || error.message || 'Failed to add user to group';
          return throwError(() => new Error(message));
        })
      );
  }

  /**
   * Removes a user from a group.
   *
   * Deletes the FGA tuple: `{ user: "{userId}", relation: "member", object: "{groupId}" }`
   *
   * @param groupId - Full FGA group identifier
   * @param userId - Full FGA user identifier
   * @returns Observable completing on success
   */
  removeUserFromGroup(groupId: string, userId: string): Observable<void> {
    const payload = {
      deletes: {
        tuple_keys: [
          {
            user: userId,
            relation: 'member',
            object: groupId,
          },
        ],
      },
    };

    return this.http
      .post<Record<string, never>>(this.getEndpoint('write'), payload, {
        headers: this.getHeaders(),
      })
      .pipe(
        map(() => undefined),
        catchError((error: HttpErrorResponse) => {
          const message =
            error.error?.error || error.error?.message || error.message || 'Failed to remove user from group';
          return throwError(() => new Error(message));
        })
      );
  }

  /**
   * Removes a user from their organisation.
   *
   * Calls `DELETE /stores/{storeId}/members/{userId}` on the FGA proxy, which
   * deletes all of the user's organisation- and group-level relationship tuples.
   * The organisation is derived from the caller's JWT `org_id` claim by the proxy.
   *
   * @param userId - Auth0 user ID without the "user:" prefix (e.g., "waad|org|abc123")
   * @returns Observable completing on success
   */
  removeOrgMember(userId: string): Observable<void> {
    return this.http
      .delete<void>(this.getEndpoint(`members/${encodeURIComponent(userId)}`), {
        headers: this.getHeaders(),
      })
      .pipe(
        map(() => undefined),
        catchError((error: HttpErrorResponse) => {
          const message =
            error.error?.error ||
            error.error?.message ||
            error.message ||
            'Failed to remove user from organisation';
          return throwError(() => new Error(message));
        })
      );
  }

  // ---------------------------------------------------------------------------
  // Group Owner Operations
  // ---------------------------------------------------------------------------

  /**
   * Adds a user as an owner (admin) of a group.
   *
   * Creates an FGA tuple: `{ user: "{userId}", relation: "admin", object: "{groupId}" }`
   *
   * @param groupId - Full FGA group identifier (e.g., "group:org|team")
   * @param userId - Full FGA user identifier (e.g., "user:waad|org|abc123")
   * @returns Observable completing on success
   */
  addOwnerToGroup(groupId: string, userId: string): Observable<void> {
    const payload = {
      writes: {
        tuple_keys: [
          {
            user: userId,
            relation: 'admin',
            object: groupId,
          },
        ],
      },
    };

    return this.http
      .post<Record<string, never>>(this.getEndpoint('write'), payload, {
        headers: this.getHeaders(),
      })
      .pipe(
        map(() => undefined),
        catchError((error: HttpErrorResponse) => {
          const message =
            error.error?.error || error.error?.message || error.message || 'Failed to add owner to group';
          return throwError(() => new Error(message));
        })
      );
  }

  /**
   * Removes a user as an owner (admin) from a group.
   *
   * Deletes the FGA tuple: `{ user: "{userId}", relation: "admin", object: "{groupId}" }`
   *
   * @param groupId - Full FGA group identifier
   * @param userId - Full FGA user identifier
   * @returns Observable completing on success
   */
  removeOwnerFromGroup(groupId: string, userId: string): Observable<void> {
    const payload = {
      deletes: {
        tuple_keys: [
          {
            user: userId,
            relation: 'admin',
            object: groupId,
          },
        ],
      },
    };

    return this.http
      .post<Record<string, never>>(this.getEndpoint('write'), payload, {
        headers: this.getHeaders(),
      })
      .pipe(
        map(() => undefined),
        catchError((error: HttpErrorResponse) => {
          const message =
            error.error?.error || error.error?.message || error.message || 'Failed to remove owner from group';
          return throwError(() => new Error(message));
        })
      );
  }

  // ---------------------------------------------------------------------------
  // Group Owner Groups Operations (Groups as Owners)
  // ---------------------------------------------------------------------------

  /**
   * Fetches groups that are owners (admins) of a specific group.
   *
   * Queries FGA for tuples matching:
   * `{ object: "{groupId}", relation: "admin", user: "group:*" }`
   *
   * This complements getGroupOwners() which fetches users as owners.
   * Per FGA common module schema, admin relation accepts: [user, group#member]
   *
   * @param groupId - Full FGA group identifier (e.g., "group:org|team")
   * @returns Observable containing group IDs that are owners of this group
   *
   * @example
   * ```typescript
   * this.fgaApi.getGroupOwnerGroups('group:org|team').subscribe(result => {
   *   console.log('Owner groups:', result.groups); // ['group:org|admins', 'group:org|managers']
   * });
   * ```
   */
  getGroupOwnerGroups(groupId: string): Observable<{ groupId: string; groups: string[]; continuationToken?: string }> {
    const payload = {
      tuple_key: {
        object: groupId,
        relation: 'admin',
      },
      page_size: 100,
    };

    return this.http
      .post<{ tuples?: { key?: { user?: string } }[]; continuation_token?: string }>(
        this.getEndpoint('read'),
        payload,
        { headers: this.getHeaders() }
      )
      .pipe(
        map(response => {
          const groups = new Set<string>();
          response.tuples?.forEach(t => {
            const u = t.key?.user;
            // Only include groups and list by group name (filter out users which start with FGA_USER_PREFIX)
            if (u?.startsWith(FGA_GROUP_PREFIX)) groups.add(u.replace('#member', ''));
          });
          return {
            groupId,
            groups: Array.from(groups).sort(),
            continuationToken: response.continuation_token,
          };
        }),
        catchError((error: HttpErrorResponse) => {
          const message =
            error.error?.error || error.error?.message || error.message || 'Failed to fetch group owner groups';
          return throwError(() => new Error(message));
        })
      );
  }

  /**
   * Adds a group as an owner (admin) of another group.
   *
   * Creates an FGA tuple: `{ user: "{ownerGroupId}", relation: "admin", object: "{groupId}" }`
   *
   * Per FGA common module schema, admin relation accepts groups as subjects.
   *
   * @param groupId - Full FGA group identifier (e.g., "group:org|team")
   * @param ownerGroupId - Full FGA group identifier for the owner group (e.g., "group:org|admins")
   * @returns Observable completing on success
   *
   * @example
   * ```typescript
   * this.fgaApi.addOwnerGroupToGroup('group:org|team', 'group:org|admins').subscribe({
   *   next: () => console.log('Owner group added'),
   *   error: (err) => console.error('Failed:', err.message)
   * });
   * ```
   */
  addOwnerGroupToGroup(groupId: string, ownerGroupId: string): Observable<void> {
    const payload = {
      writes: {
        tuple_keys: [
          {
            user: ownerGroupId + '#member',
            relation: 'admin',
            object: groupId,
          },
        ],
      },
    };

    return this.http
      .post<Record<string, never>>(this.getEndpoint('write'), payload, {
        headers: this.getHeaders(),
      })
      .pipe(
        map(() => undefined),
        catchError((error: HttpErrorResponse) => {
          const message =
            error.error?.error || error.error?.message || error.message || 'Failed to add owner group to group';
          return throwError(() => new Error(message));
        })
      );
  }

  /**
   * Removes a group as an owner (admin) from another group.
   *
   * Deletes the FGA tuple: `{ user: "{ownerGroupId}", relation: "admin", object: "{groupId}" }`
   *
   * @param groupId - Full FGA group identifier
   * @param ownerGroupId - Full FGA group identifier for the owner group to remove
   * @returns Observable completing on success
   *
   * @example
   * ```typescript
   * this.fgaApi.removeOwnerGroupFromGroup('group:org|team', 'group:org|admins').subscribe({
   *   next: () => console.log('Owner group removed'),
   *   error: (err) => console.error('Failed:', err.message)
   * });
   * ```
   */
  removeOwnerGroupFromGroup(groupId: string, ownerGroupId: string): Observable<void> {
    const payload = {
      deletes: {
        tuple_keys: [
          {
            user: ownerGroupId + '#member',
            relation: 'admin',
            object: groupId,
          },
        ],
      },
    };

    return this.http
      .post<Record<string, never>>(this.getEndpoint('write'), payload, {
        headers: this.getHeaders(),
      })
      .pipe(
        map(() => undefined),
        catchError((error: HttpErrorResponse) => {
          const message =
            error.error?.error || error.error?.message || error.message || 'Failed to remove owner group from group';
          return throwError(() => new Error(message));
        })
      );
  }

  // Note: User operations are handled by UserEnrichmentService (Auth0 Management API)


}
